import React, { useEffect, useState } from 'react';
import { getAuthorDisplayName } from '../../utils/displayName';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import { isMediaImage, isMediaVideo } from '../../utils/mediaDetect';
import { htmlToExcerpt } from '../../utils/sanitizeHtml';

import { getApiBaseUrl } from '../../config/api';

const API_URL = getApiBaseUrl();

/**
 * Facebook-style share/repost modal with optional caption.
 */
export default function RepostModal({
  open,
  post,
  user,
  initialCaption = '',
  alreadyReposted = false,
  submitting = false,
  onClose,
  onSubmit,
  onUnrepost,
}) {
  const [caption, setCaption] = useState(originalCaption(initialCaption));

  useEffect(() => {
    if (open) {
      setCaption(originalCaption(initialCaption));
    }
  }, [open, initialCaption, post?.id]);

  if (!open || !post) return null;

  const preview = post.post_type === 'repost' && post.original_post
    ? post.original_post
    : post;

  const authorName = getAuthorDisplayName(preview);
  const excerptSource =
    preview.hiring_description || preview.text || preview.content || '';
  const shortContent = htmlToExcerpt(excerptSource, 180);

  const firstMedia = Array.isArray(preview.media) && preview.media[0]
    ? preview.media[0]
    : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1300,
        padding: 16,
      }}
      onClick={() => {
        if (!submitting) onClose?.();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 16,
          width: '100%',
          maxWidth: 520,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(15, 23, 42, 0.45)',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
            {alreadyReposted ? 'Edit repost' : 'Repost'}
          </h3>
          <button
            type="button"
            disabled={submitting}
            onClick={onClose}
            style={{
              border: 'none',
              background: '#f3f4f6',
              width: 32,
              height: 32,
              borderRadius: '999px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 18,
              color: '#475569',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ padding: 18 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <img
              src={resolveAvatarUrl(user, API_URL)}
              alt=""
              onError={handleAvatarError}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                objectFit: 'cover',
                background: '#e5e7eb',
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: '#111827', fontSize: 14 }}>
                {getAuthorDisplayName(user)}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                Reposting to your feed
              </div>
            </div>
          </div>

          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Say something about this…"
            maxLength={2000}
            disabled={submitting}
            rows={4}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '12px 14px',
              fontSize: 15,
              lineHeight: 1.45,
              resize: 'vertical',
              outline: 'none',
              fontFamily: 'inherit',
              color: '#111827',
            }}
          />
          <div
            style={{
              textAlign: 'right',
              fontSize: 12,
              color: '#9ca3af',
              marginTop: 4,
              marginBottom: 14,
            }}
          >
            {caption.length}/2000
          </div>

          {/* Original post preview */}
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              overflow: 'hidden',
              background: '#fafafa',
              maxWidth: '100%',
            }}
          >
            <div style={{ padding: '12px 14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <img
                  src={resolveAvatarUrl(preview, API_URL)}
                  alt=""
                  onError={handleAvatarError}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    objectFit: 'cover',
                    background: '#e5e7eb',
                    flexShrink: 0,
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 13,
                      color: '#111827',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {authorName}
                  </div>
                  {preview.created_at && (
                    <div style={{ fontSize: 11, color: '#6b7280' }}>
                      {preview.created_at}
                    </div>
                  )}
                </div>
              </div>
              {preview.title && preview.title !== 'Repost' && (
                <div
                  style={{
                    marginTop: 8,
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#111827',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {preview.title}
                </div>
              )}
              {shortContent && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 13,
                    color: '#374151',
                    lineHeight: 1.4,
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {shortContent}
                </div>
              )}
            </div>
            {firstMedia && isMediaImage(firstMedia.url, firstMedia.type) && (
              <img
                src={firstMedia.url}
                alt=""
                style={{
                  width: '100%',
                  maxHeight: 180,
                  objectFit: 'cover',
                  display: 'block',
                  borderTop: '1px solid #e5e7eb',
                }}
              />
            )}
            {firstMedia && isMediaVideo(firstMedia.url, firstMedia.type) && (
              <div
                style={{
                  padding: '10px 14px',
                  borderTop: '1px solid #e5e7eb',
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                Video attachment
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: alreadyReposted ? 'space-between' : 'flex-end',
            gap: 10,
            padding: '12px 18px 16px',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          {alreadyReposted && (
            <button
              type="button"
              disabled={submitting}
              onClick={() => onUnrepost?.()}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#dc2626',
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? 'not-allowed' : 'pointer',
                padding: '10px 12px',
              }}
            >
              Undo repost
            </button>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={submitting}
              onClick={onClose}
              style={{
                border: '1px solid #e5e7eb',
                background: '#fff',
                color: '#374151',
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 999,
                padding: '10px 16px',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={() => onSubmit?.(caption.trim())}
              style={{
                border: 'none',
                background: submitting ? '#93c5fd' : '#0a66c2',
                color: '#fff',
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 999,
                padding: '10px 18px',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting
                ? 'Posting…'
                : alreadyReposted
                  ? 'Update'
                  : 'Repost now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function originalCaption(value) {
  return value == null ? '' : String(value);
}
